import React, { useContext, useEffect, useState } from 'react';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import {
  IconButton,
  ListItem,
  Tooltip,
  Typography,
  Box,
  Grid,
  SwipeableDrawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import styled from '@emotion/styled';
import MuiDrawer from '@mui/material/Drawer';
import { NavLink } from 'react-router-dom';
import Home from '@mui/icons-material/HomeOutlined';
import { makeStyles, withStyles } from '@mui/styles';
import NewApplication from '@mui/icons-material/NoteAlt';
import MyApplication from '@mui/icons-material/SpeakerNotes';
import FeePayment from '@mui/icons-material/Payment';
import Document from '@mui/icons-material/AssignmentInd';
import History from '@mui/icons-material/ManageHistory';
import Ken42Icon from '../../Assets/SvgImages/Ken42.svg';
import spjain from '../../Assets/SvgImages/spjain.svg';
import spjainDetailed from '../../Assets/Images/spjainDetailed.png';
import { AuthContext } from '../../Context/authContext';
import routes from '../../Constants/routes';
import FacebookIcon from '../../Assets/SvgImages/FacebookIcon.svg';
import LinkedinIcon from '../../Assets/SvgImages/LinkedinIcon.svg';
import HomeNav from '../../Assets/Svg/home.svg';
import MyApplicationNav from '../../Assets/Svg/applications.svg';
import PaymentNav from '../../Assets/Svg/payment.svg';
import DocumentNav from '../../Assets/Svg/document.svg';
import TwitterIcon from '../../Assets/SvgImages/TwitterIcon.svg';
import OfferLetterIcon from '../../Assets/SvgImages/OfferLetterIcon.svg';
import clsx from 'clsx';
import ExitToAppOutlinedIcon from '@mui/icons-material/ExitToAppOutlined';
import { TABLET_BREAKPOINT } from '../../Constants/constant';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { getDocuments, getRenderingDetails } from '../../Utils/apiServices';

const drawerWidth = 210;

const useStyles = makeStyles((theme) => ({
  listItem: {
    padding: '12px 20px',
    width: 'calc(100% + 20px)',
    marginBottom: 8,
    '&:hover': {
      width: 'calc(100% + 20px)',
      backgroundColor: theme.palette.KenColors.sideNavItemHoverBackground,
      borderRadius: '24px 0px 0px 24px',

      '& $subLevel': {
        color: theme.palette.KenColors.sideNavItemHoverColor,
      },
      '& $heading': {
        color: theme.palette.KenColors.sideNavItemHoverColor,
      },
    },
    '&.Mui-selected': {
      backgroundColor: 'turquoise',
      color: 'white',
      fontWeight: 600,
      borderRadius: '24px 0px 0px 24px',
    },
    borderRadius: '24px 0px 0px 24px',
  },
  active: {
    '& $listItem': {
      width: 'calc(100% + 20px)',
      backgroundColor: '#4290BD',
      borderRadius: '24px 0px 0px 24px',
    },

    '& $subListItem': {
      borderLeft: `4px solid ${theme.palette.KenColors.kenWhite}`,
      marginLeft: 0,
      backgroundColor: theme.palette.KenColors.sideNavItemActiveBackground,
      borderRadius: '24px 0px 0px 24px',
    },

    '& $heading': {
      color: theme.palette.KenColors.sideNavItemActiveColor,
    },
    '& $subLevel': {
      color: theme.palette.KenColors.sideNavItemActiveColor,
    },
    '&.Mui-selected': {
      backgroundColor: theme.palette.KenColors.sideNavItemActiveBackground,
      color: 'white',
      fontWeight: 600,
      borderRadius: '24px 0px 0px 24px',

      '&:hover': {
        backgroundColor: theme.palette.KenColors.sideNavItemActiveBackground,
        // color: 'white',
        borderRadius: '24px 0px 0px 24px',
      },
    },
    textDecoration: 'none',
    borderRadius: '24px 0px 0px 24px',
  },
  menuIcon: {
    color: theme.palette.KenColors.kenWhite,

    '& >p': {
      display: 'flex',
      color: theme.palette.KenColors.kenWhite,
    },
  },
  heading: {
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '0.875rem',
    lineHeight: '20px',
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.KenColors.kenWhite,
  },
  drawer: {
    justifyContent: 'space-between',
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    background: 'linear-gradient(176.94deg, #158ABB 14.53%, #060B6B 95.84%)',
    margin: 16,
    borderRadius: 12,
    height: '95vh',
    [theme.breakpoints.down('md')]: {
      margin: 0,
      borderRadius: '0px 24px 24px 0px',
      height: '100vh',
      'overflow-x': 'hidden',
    },
  },
  root: {
    [theme.breakpoints.down(TABLET_BREAKPOINT)]: {
      // Note: added so side bar drawer show on top of header when active
      'z-index': '1302',
    },
  },
  icon: {
    color: theme.palette.KenColors.kenWhite,
  },
  link: {
    textDecoration: 'none',
  },
  typoReserved: {
    fontSize: 10,
    color: theme.palette.KenColors.kenWhite,
  },
  typoReservedOpen: {
    width: 88,
    whiteSpace: 'initial',
  },
  imageBanner: {
    width: 76,
    height: 47,
    marginLeft: 8,
  },
  imageBannerDetailed: {
    width: 166,
    height: 85,
    marginLeft: 8,
  },
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const DrawerFooter = styled('div', {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  opacity: 1,
  marginBottom: theme.spacing(3),
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  display: 'relative',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(8)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(12)} + 1px)`,
  },
});

const LightTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 14,
  },
}))(Tooltip);

const menu = [
  // {
  //   icon: <img src={HomeNav} alt="" />,
  //   label: 'Home',
  //   link: `/${routes.home}`,
  //   callback: '',
  // },

  // {
  //   icon: <img src={MyApplicationNav} alt="" />,
  //   label: 'My Applications',
  //   link: `/${routes.myApplications}`,
  //   callback: '',
  // },
  {
    icon: <img src={HomeNav} alt="" />,
    label: 'Home',
    link: `/${routes.home}`,
    callback: '',
  },
  // {
  //   icon: <img src={PaymentNav} alt="" />,
  //   label: 'Payment',
  //   link: `/${routes.payment}`,
  //   callback: '',
  // }

  // {
  //   icon: <ChatBubbleOutlineIcon />,
  //   label: 'Interview',
  //   link: `/${routes.interview}`,
  //   callback: '',
  // },
];

const GetSideNavData = (data) => {
  const classes = useStyles();
  const { open, handleDrawerClose, handleDrawerOpen, sideNavMenu } = data;
  const theme = useTheme();

  return (
    <>
      <Box>
        <DrawerHeader>
          <Grid container direction="column">
            <Grid
              item
              style={{ textAlign: 'left', marginTop: 16, minHeight: 92 }}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <img
                src={spjainDetailed}
                alt="Ken42 Icon"
                className={clsx(classes.imageBanner, {
                  [classes.imageBannerDetailed]: open === true,
                })}
              />
            </Grid>
            <Grid
              item
              style={{ textAlign: 'right', marginRight: -12, paddingTop: 16 }}
              sx={{ display: { xs: 'none', tablet: 'block' } }}
            >
              <IconButton onClick={open ? handleDrawerClose : handleDrawerOpen}>
                {!open ? (
                  <KeyboardDoubleArrowRightIcon className={classes.icon} />
                ) : (
                  <KeyboardDoubleArrowLeftIcon className={classes.icon} />
                )}
              </IconButton>
            </Grid>
          </Grid>
        </DrawerHeader>
        <List
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingLeft: open ? '40px' : undefined,
          }}
        >
          {sideNavMenu &&
            sideNavMenu?.map((item, index) => {
              return (
                <NavLink
                  key={index}
                  to={
                    item?.link === null || item?.link === undefined ? (
                      <Typography>{item?.link}</Typography>
                    ) : (
                      item?.link
                    )
                  }
                  className={({ isActive }) =>
                    isActive ? classes.active : classes.link
                  }
                >
                  <ListItem
                    onClick={item.callback}
                    classes={{
                      root: classes.listItem,
                      selected: classes.active,
                    }}
                    button
                  >
                    <Grid
                      container
                      alignItems="center"
                      sx={{ minWidth: open ? 180 : undefined }}
                    >
                      <Grid item sx={{ minWidth: open ? '32px' : undefined }}>
                        {open === false ? (
                          <LightTooltip
                            title={item.label}
                            placement="right-end"
                          >
                            <span className={classes.menuIcon}>
                              <Typography>{item.icon}</Typography>
                            </span>
                          </LightTooltip>
                        ) : (
                          <span className={classes.menuIcon}>
                            <Typography>{item.icon}</Typography>
                          </span>
                        )}
                      </Grid>
                      <Grid item>
                        {open && (
                          <span className={classes.heading}>{item.label}</span>
                        )}
                      </Grid>
                    </Grid>
                  </ListItem>
                </NavLink>
              );
            })}
        </List>
      </Box>
      <DrawerFooter open={open}>
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          direction="column"
        >
          <Grid item marginBottom={theme.spacing(2)}>
            <>
              <Grid
                container
                spacing={open ? 2 : 1}
                direction={open ? 'row' : 'column'}
              >
                <Grid item display="flex" alignItems="flex-end">
                  <a
                    href="https://www.facebook.com/spjainschoolofglobalmanagement"
                    target="_blank"
                  >
                    <img src={FacebookIcon} />
                  </a>
                </Grid>
                <Grid item display="flex" alignItems="flex-end">
                  <a
                    href="https://twitter.com/spjainglobal?lang=en"
                    target="_blank"
                  >
                    <img src={TwitterIcon} />
                  </a>
                </Grid>
                <Grid item display="flex" alignItems="flex-end">
                  <a
                    href="https://in.linkedin.com/school/s-p-jain-school-of-global-management"
                    target="_blank"
                  >
                    <img src={LinkedinIcon} />
                  </a>
                </Grid>
              </Grid>
            </>
          </Grid>
          <Grid item>
            <Typography
              className={clsx(classes.typoReserved, {
                [classes.typoReservedOpen]: open === false,
              })}
            >
              © 2022 All rights Reserved
            </Typography>
          </Grid>
        </Grid>
      </DrawerFooter>
    </>
  );
};

export default function SideNavigation(props) {
  const [sideNavMenu, setSideNavMenu] = useState(menu);
  const [checkScreenSize, setCheckScreenSize] = useState(false);
  const classes = useStyles();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(
    theme.breakpoints.down(TABLET_BREAKPOINT)
  );
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    state: { user },
    dispatch,
  } = useContext(AuthContext);
  const { open, setSwipeableDrawer, swipeableDrawer, setOpen } = props;

  useEffect(() => {
    let temp = [...sideNavMenu];
    if (
      smallScreen &&
      (sideNavMenu || []).filter((s) => s.label === 'Logout').length === 0
    ) {
      temp.push({
        icon: <ExitToAppOutlinedIcon />,
        label: 'Logout',
        link: ``,
        callback: () => {
          dispatch({
            type: 'LOGOUT',
            payload: {
              user: {},
              token: null,
            },
          });
        },
      });
    }
    setSideNavMenu([...temp]);
  }, [isMobileScreen, smallScreen, checkScreenSize]);

  console.log(
    smallScreen,
    (sideNavMenu || []).filter((s) => s.label === 'Logout').length === 0
  );

  useEffect(() => {
    getRenderingDetails(user?.applicantId).then((res) => {
      const tempData = [...sideNavMenu];
      if (res?.data) {
        const {
          renderDocuments,
          renderFeePayment,
          renderOfferLetter,
          renderQuestionnaire,
        } = res?.data;

        if (
          renderDocuments === 'true' &&
          sideNavMenu?.filter((s) => s.label === 'Documents')?.length === 0
        ) {
          tempData.push({
            icon: <img src={DocumentNav} alt="" />,
            label: 'Documents',
            link: `/${routes.document}`,
            callback: isMobileScreen
              ? () => {
                  setSwipeableDrawer(false);
                }
              : '',
          });
        }

        if (
          renderFeePayment === 'true' &&
          sideNavMenu?.filter((s) => s.label === 'Fee Payments')?.length === 0
        ) {
          tempData.push({
            icon: <img src={PaymentNav} alt="" />,
            label: 'Fee Payments',
            link: `/${routes.feePayments}`,
            callback: isMobileScreen
              ? () => {
                  setSwipeableDrawer(false);
                }
              : '',
          });
        }

        if (
          renderOfferLetter === 'true' &&
          sideNavMenu?.filter((s) => s.label === 'Offer Letter')?.length === 0
        ) {
          tempData.push({
            icon: <img src={OfferLetterIcon} alt="" />,
            label: 'Offer Letter',
            link: `/${routes.offerLetter}`,
            callback: isMobileScreen
              ? () => {
                  setSwipeableDrawer(false);
                }
              : '',
          });
        }

        if (
          renderQuestionnaire === 'true' &&
          sideNavMenu?.filter((s) => s.label === 'Offer Letter')?.length === 0
        ) {
          tempData.push({
            icon: <img src={OfferLetterIcon} alt="" />,
            label: 'Onboarding',
            link: `/${routes.onboardQues}`,
            callback: isMobileScreen
              ? () => {
                  setSwipeableDrawer(false);
                }
              : '',
          });
        }

        setSideNavMenu([...tempData]);
        setCheckScreenSize(true);
      }
    });
  }, []);

  useEffect(() => {
    if (isMobileScreen) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [isMobileScreen]);

  const toggleDrawer = (event) => {
    setSwipeableDrawer(false);
  };

  return (
    <Box sx={{ m: isMobileScreen ? undefined : 2 }}>
      {!isMobileScreen ? (
        <Drawer
          variant="permanent"
          open={open}
          classes={{ paper: classes.drawer, root: classes.root }}
        >
          <GetSideNavData sideNavMenu={sideNavMenu} {...props} />
        </Drawer>
      ) : (
        <SwipeableDrawer
          anchor={'left'}
          open={swipeableDrawer}
          onClose={toggleDrawer}
          classes={{ paper: classes.drawer, root: classes.root }}
        >
          <GetSideNavData sideNavMenu={sideNavMenu} {...props} />
        </SwipeableDrawer>
      )}
    </Box>
  );
}
